// "Is this car even worth repairing?" — an opt-in card on the results page, the
// sibling of ClaimOrCash and built to the same rules: collapsed to a headline
// and a button until the user asks for it, so the reader who only wanted a
// price pays one line of scroll for it.
//
// Why here and not in the wizard: the repair range is the numerator of this
// question, so it can't be asked earlier, and the denominator — what the car is
// worth — is a question most people don't want asked at all. Behind the button
// it's ONE question for anyone who knows their car's value; only "I'm not sure"
// opens the two follow-ups that feed the depreciation fallback.
//
// Nothing here is sent to the server or written into the shared report: what
// your car is worth is your business, not the crash's.
import { useEffect, useState } from "react";
import type {
  Estimate,
  TotalLossAnswers,
  TotalLossAssessment,
  TotalLossMath,
  TotalLossQuestionId,
  VehicleValueRange,
} from "../shared/types";
import {
  TOTAL_LOSS_COPY,
  TOTAL_LOSS_QUESTIONS,
  assessTotalLoss,
  nextTotalLossQuestion,
  resetTotalLossFrom,
  resolveValue,
  totalLossApplies,
  totalLossIsQuiet,
} from "../shared/totalLoss";
import {
  CONDITION_ANSWERS,
  MILEAGE_BANDS,
  VALUE_BUCKETS,
  VALUE_UNKNOWN_ID,
} from "../shared/questionnaire";

const usd = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const pct = (n: number) => `${Math.round(n * 100)}%`;

// Where the repair sits against the car's value. This is the whole card in one
// picture: the band is what the repair might cost as a share of the car, the
// first marker is where carriers stop repairing, the second is the car's entire
// value. A band crossing the first marker IS the borderline verdict.
function RatioBar({ math }: { math: TotalLossMath }) {
  // Cap the axis at twice the car's value. A wrecked $2k car can ratio out at
  // 1900%, and scaling to that would squash the total-loss line and the car's
  // own value into the first two pixels — losing the only two reference points
  // the picture exists to show. The band clips instead, squared off at the
  // right edge to read as "continues past here", and the legend still prints
  // the true figures.
  const max = Math.min(Math.max(math.ratioRange.high, 1.15), 2) * 1.08;
  const at = (n: number) => `${Math.max(0, Math.min(100, (n / max) * 100))}%`;
  const clipped = math.ratioRange.high > max;
  return (
    <div className="tl-bar">
      <div className="tl-track">
        <div
          className={`tl-range${clipped ? " tl-range-clipped" : ""}`}
          style={{
            left: at(math.ratioRange.low),
            width: at(Math.max(0, Math.min(math.ratioRange.high, max) - math.ratioRange.low)),
          }}
        />
        <div className="tl-mark tl-mark-threshold" style={{ left: at(math.insurerThresholdPct) }} />
        <div className="tl-mark tl-mark-full" style={{ left: at(1) }} />
      </div>
      <div className="tl-legend">
        <span className="tl-key tl-key-range">
          Repair {pct(math.ratioRange.low)} – {pct(math.ratioRange.high)} of value
        </span>
        <span className="tl-key tl-key-threshold">
          Total-loss line {pct(math.insurerThresholdPct)}
        </span>
        <span className="tl-key tl-key-full">Car's full value</span>
      </div>
    </div>
  );
}

function MathTable({ math }: { math: TotalLossMath }) {
  return (
    <details className="tl-math">
      <summary>How we got {pct(math.ratioMid)}</summary>
      <dl>
        <div>
          <dt>Repair range{math.supplementIncluded ? " (incl. expected supplement)" : ""}</dt>
          <dd>
            {usd(math.repairRange.low)} – {usd(math.repairRange.high)}
          </dd>
        </div>
        <div>
          <dt>
            Vehicle value
            {math.value.source === "estimated" ? " (estimated)" : " (yours)"}
          </dt>
          <dd>
            {usd(math.value.low)} – {usd(math.value.high)}
          </dd>
        </div>
        <div className="tl-math-total">
          <dt>Total-loss line</dt>
          <dd>
            {pct(math.insurerThresholdPct)} ≈ {usd(math.thresholdDollars)} of repairs
          </dd>
        </div>
      </dl>
      {math.statutoryThresholdPct !== undefined && (
        <p className="sub">
          Separately, {math.stateName} brands the title salvage at{" "}
          {pct(math.statutoryThresholdPct)}, which is a different rule from the
          carrier's decision to total.
        </p>
      )}
      <p className="sub">
        Carriers set their own trigger and it moves with the salvage market.{" "}
        {pct(math.insurerThresholdPct)} is a common rule of thumb, not your carrier's
        published figure.
      </p>
    </details>
  );
}

function Verdict({
  assessment,
  onReset,
}: {
  assessment: TotalLossAssessment;
  onReset: () => void;
}) {
  const { math } = assessment;
  return (
    <div className={`tl-verdict tl-${assessment.status}`}>
      <h3>{assessment.headline}</h3>
      <p className="tl-summary">{assessment.summary}</p>

      {math && <RatioBar math={math} />}
      {math && <MathTable math={math} />}

      <ul className="tl-points">
        {assessment.points.map((p, i) => (
          <li key={i}>{p}</li>
        ))}
      </ul>

      <p className="tl-next">
        <b>Next step:</b> {assessment.nextStep}
      </p>

      <p className="tl-disclaimer">{assessment.disclaimer}</p>

      <button className="tl-restart" onClick={onReset}>
        Change my answers
      </button>
    </div>
  );
}

// Answered-question chips, tappable to re-answer. Same contract as the claim
// card: re-answering drops everything downstream, because the follow-ups are
// only reachable through the value question.
function chipLabel(q: TotalLossQuestionId, a: TotalLossAnswers): string | null {
  if (q === "value") {
    if (!a.valueSource) return null;
    if (a.valueSource === "estimated") return "Estimate it for me";
    if (a.valueSource === "user-exact" && a.valueLow != null) return usd(a.valueLow);
    const bucket = VALUE_BUCKETS.find((b) => b.low === a.valueLow && b.high === a.valueHigh);
    return bucket?.label ?? null;
  }
  if (q === "mileage")
    return a.mileage ? (MILEAGE_BANDS.find((o) => o.id === a.mileage)?.label ?? null) : null;
  return a.condition
    ? (CONDITION_ANSWERS.find((o) => o.id === a.condition)?.label ?? null)
    : null;
}

export function TotalLoss({
  estimate,
  zip,
  onVehicleValue,
}: {
  estimate: Estimate;
  zip?: string | null;
  // Lifted so the claim-or-cash card can use the same figure instead of asking
  // for it a second time on the same page.
  onVehicleValue?: (v: VehicleValueRange | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [answers, setAnswers] = useState<TotalLossAnswers>({});

  const applies = totalLossApplies(estimate);
  const resolved = applies ? resolveValue(estimate, answers, new Date().getFullYear()) : null;

  // Publish upward on the primitives, not the object — resolveValue builds a
  // fresh one every render, so depending on it directly would loop forever.
  const vLow = resolved?.low;
  const vHigh = resolved?.high;
  const vSource = resolved?.source;
  useEffect(() => {
    if (!onVehicleValue) return;
    onVehicleValue(
      vLow != null && vHigh != null && vSource
        ? { low: vLow, high: vHigh, currency: "USD", source: vSource }
        : null,
    );
  }, [vLow, vHigh, vSource, onVehicleValue]);

  // Nothing to weigh on the engine's inspection-only path: no repair scope means
  // no ratio, and a verdict would be invented rather than computed.
  if (!applies) return null;

  if (!open) {
    const quiet = totalLossIsQuiet(estimate);
    return (
      <div className={`card tl-card${quiet ? " tl-quiet" : ""}`}>
        <h2>{TOTAL_LOSS_COPY.teaserHeadline}</h2>
        <p className="sub">
          {quiet ? TOTAL_LOSS_COPY.teaserSummaryQuiet : TOTAL_LOSS_COPY.teaserSummary}
        </p>
        <div className="estimate-cta">
          <button className="cta" onClick={() => setOpen(true)}>
            {TOTAL_LOSS_COPY.teaserButton}
          </button>
        </div>
      </div>
    );
  }

  const pending = nextTotalLossQuestion(answers);
  const answered = (["value", "mileage", "condition"] as TotalLossQuestionId[]).filter(
    (q) => chipLabel(q, answers) !== null,
  );

  return (
    <div className="card tl-card">
      <h2>{TOTAL_LOSS_COPY.teaserHeadline}</h2>

      {answered.length > 0 && (
        <div className="tl-chips">
          {answered.map((q) => (
            <button
              key={q}
              className="tl-chip"
              onClick={() => setAnswers(resetTotalLossFrom(answers, q))}
              title="Change this answer"
            >
              {chipLabel(q, answers)}
              <span aria-hidden="true"> ✕</span>
            </button>
          ))}
        </div>
      )}

      {pending ? (
        <div className="tl-q">
          <h3>{TOTAL_LOSS_QUESTIONS[pending].prompt}</h3>
          <p className="sub">{TOTAL_LOSS_QUESTIONS[pending].hint}</p>
          <div className="options">
            {pending === "value" && (
              <>
                {VALUE_BUCKETS.map((b) => (
                  <button
                    key={b.id}
                    className="opt"
                    onClick={() =>
                      setAnswers({
                        valueLow: b.low,
                        valueHigh: b.high,
                        valueSource: "user-bucket",
                      })
                    }
                  >
                    <span>{b.label}</span>
                  </button>
                ))}
                <button
                  key={VALUE_UNKNOWN_ID}
                  className="opt"
                  onClick={() => setAnswers({ valueSource: "estimated" })}
                >
                  <span>I'm not sure</span>
                  <span className="opt-hint">
                    Two quick questions and we'll estimate it roughly
                  </span>
                </button>
              </>
            )}
            {pending === "mileage" &&
              MILEAGE_BANDS.map((o) => (
                <button
                  key={o.id}
                  className="opt"
                  onClick={() => setAnswers((prev) => ({ ...prev, mileage: o.id }))}
                >
                  <span>{o.label}</span>
                  {o.hint && <span className="opt-hint">{o.hint}</span>}
                </button>
              ))}
            {pending === "condition" &&
              CONDITION_ANSWERS.map((o) => (
                <button
                  key={o.id}
                  className="opt"
                  onClick={() => setAnswers((prev) => ({ ...prev, condition: o.id }))}
                >
                  <span>{o.label}</span>
                  {o.hint && <span className="opt-hint">{o.hint}</span>}
                </button>
              ))}
          </div>
        </div>
      ) : (
        <Verdict
          assessment={assessTotalLoss(estimate, answers, { zip })}
          onReset={() => setAnswers({})}
        />
      )}
    </div>
  );
}
